import { apiClient, objectToQueryString } from "@wtf/shared/api/utils/api-client";
import {
  UserSummaryDto,
  ProfileDto,
  GetUsersParams,
  UpdateUserRequest,
  PaginatedUsersResponse,
  SubscribersResponse,
  SubscriptionsResponse,
  UserProductsResponse,
  UserLooksResponse,
  UserCollectionsResponse,
  AccountDeletionRequest,
  UserPreferencesDto,
} from "@wtf/shared";

const BASE_ENDPOINT = "api/users";

/**
 * Get all users with pagination
 */
export async function getUsers(params: GetUsersParams = {}): Promise<PaginatedUsersResponse> {
  const queryString = objectToQueryString(params);
  return apiClient<PaginatedUsersResponse>(`${BASE_ENDPOINT}${queryString}`);
}

/**
 * Update a user (only accessible by the user themselves)
 */
export async function updateUser(id: string, data: UpdateUserRequest): Promise<UserSummaryDto> {
  return apiClient<UserSummaryDto>(`${BASE_ENDPOINT}/${id}`, "PUT", data);
}

/**
 * Request account deletion
 */
export async function requestAccountDeletion(id: string, reason?: string): Promise<AccountDeletionRequest> {
  return apiClient<AccountDeletionRequest>(`${BASE_ENDPOINT}/${id}/deletion-request`, "POST", { reason });
}

/**
 * Cancel account deletion request
 */
export async function cancelAccountDeletion(id: string): Promise<AccountDeletionRequest> {
  return apiClient<AccountDeletionRequest>(`${BASE_ENDPOINT}/${id}/deletion-request`, "DELETE");
}

/**
 * Get account deletion status
 */
export async function getAccountDeletionStatus(id: string): Promise<AccountDeletionRequest> {
  return apiClient<AccountDeletionRequest>(`${BASE_ENDPOINT}/${id}/deletion-request`, "GET");
}

// Cache for known private profiles to prevent continuous 403 requests
const privateProfileCache = new Set<string>();

/**
 * Get a user's profile
 * Returns a full profile if viewing own profile, or a limited public profile if viewing another user's profile
 */
export async function getUserProfile(id: string): Promise<ProfileDto> {
  // Check if this user is already known to be private
  if (privateProfileCache.has(id)) {
    const error = new Error("Private profile - access denied") as Error & {
      status?: number;
    };
    error.status = 403;
    throw error;
  }

  try {
    return await apiClient<ProfileDto>(`${BASE_ENDPOINT}/${id}/profile`);
  } catch (error: any) {
    // If we get a 403, cache this user as private to prevent future requests
    if (error.status === 403 || error.message?.includes("403") || error.message?.includes("Forbidden")) {
      console.log(`🔒 Caching user ${id} as private profile to prevent future requests`);
      privateProfileCache.add(id);
    }
    throw error;
  }
}

/**
 * Get a user's subscribers (followers)
 */
export async function getUserSubscribers(
  id: string,
  params: { page?: number; limit?: number } = {}
): Promise<SubscribersResponse> {
  const queryString = objectToQueryString(params);
  return apiClient<SubscribersResponse>(`${BASE_ENDPOINT}/${id}/subscribers${queryString}`);
}

/**
 * Get users that a user subscribes to (following)
 */
export async function getUserSubscribedTo(
  id: string,
  params: { page?: number; limit?: number } = {}
): Promise<SubscriptionsResponse> {
  const queryString = objectToQueryString(params);
  return apiClient<SubscriptionsResponse>(`${BASE_ENDPOINT}/${id}/subscribedTo${queryString}`);
}

/**
 * Check if a username is available
 */
export async function checkUsernameAvailability(username: string): Promise<{ username: string; isAvailable: boolean }> {
  return apiClient<{ username: string; isAvailable: boolean }>(
    `${BASE_ENDPOINT}/username/check?username=${encodeURIComponent(username)}`
  );
}

/**
 * Get all products created by a user (only accessible by the user themselves)
 */
export async function getUserProducts(
  id: string,
  params: { page?: number; limit?: number } = {}
): Promise<UserProductsResponse> {
  const queryString = objectToQueryString(params);
  return apiClient<UserProductsResponse>(`${BASE_ENDPOINT}/${id}/products${queryString}`);
}

/**
 * Get all looks created by a user (only accessible by the user themselves)
 */
export async function getUserLooks(
  id: string,
  params: { page?: number; limit?: number } = {}
): Promise<UserLooksResponse> {
  const queryString = objectToQueryString(params);
  return apiClient<UserLooksResponse>(`${BASE_ENDPOINT}/${id}/looks${queryString}`);
}

/**
 * Get all collections created by a user (only accessible by the user themselves)
 */
export async function getUserCollections(
  id: string,
  params: { page?: number; limit?: number } = {}
): Promise<UserCollectionsResponse> {
  const queryString = objectToQueryString(params);
  return apiClient<UserCollectionsResponse>(`${BASE_ENDPOINT}/${id}/collections${queryString}`);
}

/**
 * Get current user's preferences (lightweight)
 * Returns stylePreference and preferredCurrency
 */
export async function getMyPreferences(): Promise<UserPreferencesDto> {
  return apiClient<UserPreferencesDto>(`${BASE_ENDPOINT}/me/preferences`);
}

/**
 * Get a user's summary (public info only)
 */
export async function getUserSummary(id: string): Promise<UserSummaryDto> {
  return apiClient<UserSummaryDto>(`${BASE_ENDPOINT}/${id}/summary`);
}

export const usersApi = {
  getUsers,
  updateUser,
  requestAccountDeletion,
  cancelAccountDeletion,
  getAccountDeletionStatus,
  getUserProfile,
  getUserSubscribers,
  getUserSubscribedTo,
  checkUsernameAvailability,
  getUserProducts,
  getUserLooks,
  getUserCollections,
  getUserSummary,
  getMyPreferences,
};
